import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import PageShell from "../components/PageShell";
import PageHero from "../components/PageHero";
import CallToAction from "../components/CallToAction";

const TIERS = [
  {
    key: "bronze",
    name: "Bronze",
    price: "$250",
    cadence: "/ month",
    blurb: "Back our open-source tools and field notes.",
    perks: [
      "Logo on /sponsors page",
      "Mention in our quarterly digest",
      "Early access to OSS releases",
    ],
    featured: false,
  },
  {
    key: "silver",
    name: "Silver",
    price: "$1,000",
    cadence: "/ month",
    blurb: "Direct support + visibility for tools you actually use.",
    perks: [
      "Everything in Bronze",
      "Logo on the Ulties.com homepage footer strip",
      "Two pairing sessions / quarter with a Principal",
      "Custom shout-out in the next blog post",
    ],
    featured: true,
  },
  {
    key: "gold",
    name: "Gold",
    price: "$5,000",
    cadence: "/ month",
    blurb: "Co-fund a roadmap milestone. Get named on the release.",
    perks: [
      "Everything in Silver",
      "Named co-sponsor on a chosen release",
      "Quarterly private engineering deep-dive",
      "Direct line to a Principal engineer",
    ],
    featured: false,
  },
  {
    key: "custom",
    name: "Custom",
    price: "Let's talk",
    cadence: "",
    blurb: "Funding a piece of research, a dataset, or a conference?",
    perks: [
      "Tailored package",
      "Co-authored writing",
      "Multi-year commitments",
      "Bring your asks",
    ],
    featured: false,
  },
];

const REASONS = [
  {
    n: "01",
    t: "We build in the open.",
    d: "Our internal tools — agent eval harnesses, threat-model libraries, design system primitives — get released as we go. Your sponsorship makes that sustainable.",
  },
  {
    n: "02",
    t: "We write what we learn.",
    d: "47 products in. Every essay, talk and field note we publish is bought-and-paid-for engineering experience, not theory.",
  },
  {
    n: "03",
    t: "We back early-stage teams.",
    d: "10% of sponsorship goes into a pro-bono pool for early-stage founders working on tools that move the field forward.",
  },
];

const FAQ = [
  {
    q: "Is sponsorship the same as hiring you?",
    a: "No. Hiring engages the studio for paid client work. Sponsorship funds the open-source tools, writing and research we publish freely.",
  },
  {
    q: "How is the money used?",
    a: "Roughly 70% goes to engineer time on OSS and research, 20% to events / writing infrastructure, and 10% to our pro-bono pool for early-stage founders.",
  },
  {
    q: "Can companies in regulated industries sponsor?",
    a: "Yes. We sign sponsor agreements and provide quarterly statements suitable for procurement and audit.",
  },
  {
    q: "Can we sponsor anonymously?",
    a: "Absolutely. You'll still get the perks; we just won't list you anywhere public.",
  },
];

export default function SponsorPage() {
  const [tier, setTier] = useState("silver");
  const [email, setEmail] = useState("");

  const onIntent = (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Drop your email so we can follow up.");
    toast.success("Got it — we'll reach out within 24 hours.");
    setEmail("");
  };

  return (
    <PageShell>
      <PageHero
        eyebrow="◇ Sponsor"
        title="Back the studio."
        accent="studio."
        kicker="Ulties releases the tools, writing and research we use to build software that has to work. If our open work has saved you time, consider sponsoring."
      />

      {/* Why */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16">
            <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
              ◇ Why sponsor
            </span>
            <h2 className="mt-4 font-display text-5xl md:text-7xl font-medium tracking-tight leading-[0.95] text-primary-x max-w-3xl">
              What your money does.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {REASONS.map((r, i) => (
              <motion.article
                key={r.n}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: i * 0.08, duration: 0.7 }}
                data-testid={`sponsor-reason-${r.n}`}
                className="rounded-2xl border border-hairline bg-glass p-8 md:p-10"
              >
                <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-8">
                  {r.n}
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-primary-x">
                  {r.t}
                </h3>
                <p className="mt-5 text-secondary-x leading-relaxed">{r.d}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-24 md:py-32 bg-surface border-y border-hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
                ◇ Tiers
              </span>
              <h2 className="mt-4 font-display text-5xl md:text-7xl font-medium tracking-tight leading-[0.95] text-primary-x">
                Pick a level.
              </h2>
            </div>
            <p className="md:max-w-sm text-secondary-x leading-relaxed">
              Tiers are deliberately simple. Pay monthly, pause anytime. Custom
              packages are negotiable.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {TIERS.map((t, i) => {
              const active = tier === t.key;
              return (
                <motion.button
                  key={t.key}
                  type="button"
                  onClick={() => setTier(t.key)}
                  data-testid={`sponsor-tier-${t.key}`}
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ delay: i * 0.06, duration: 0.6 }}
                  className={`relative text-left rounded-2xl border p-8 transition-colors ${
                    active
                      ? "border-transparent"
                      : "border-hairline bg-glass hover:border-hairline-strong"
                  }`}
                  style={
                    active
                      ? { background: "var(--text-primary)", color: "var(--canvas)" }
                      : {}
                  }
                >
                  {t.featured && !active && (
                    <span className="absolute top-4 right-4 text-[10px] tracking-[0.22em] uppercase font-mono-x text-muted-x">
                      Most popular
                    </span>
                  )}
                  <div
                    className={`text-[11px] tracking-[0.22em] uppercase font-mono-x mb-6 ${
                      active ? "" : "text-muted-x"
                    }`}
                    style={active ? { color: "var(--surface)" } : {}}
                  >
                    Tier {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3
                    className={`font-display text-3xl md:text-4xl font-medium tracking-tight ${
                      active ? "" : "text-primary-x"
                    }`}
                  >
                    {t.name}
                  </h3>
                  <div className="mt-5 flex items-baseline gap-1">
                    <span className="font-display text-3xl">{t.price}</span>
                    {t.cadence && (
                      <span
                        className={`text-sm ${active ? "" : "text-muted-x"}`}
                        style={active ? { color: "var(--surface)" } : {}}
                      >
                        {t.cadence}
                      </span>
                    )}
                  </div>
                  <p
                    className={`mt-5 leading-relaxed text-sm ${
                      active ? "" : "text-secondary-x"
                    }`}
                  >
                    {t.blurb}
                  </p>
                  <ul className="mt-8 space-y-3 text-sm">
                    {t.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-2">
                        <span className="mt-1.5 inline-block h-1 w-3 rounded-full" style={{ background: active ? "var(--canvas)" : "var(--text-primary)" }} />
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lead capture */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
            ◇ Express interest
          </span>
          <h2 className="mt-4 font-display text-4xl md:text-6xl font-medium tracking-tight leading-[0.95] text-primary-x">
            Selected: <span className="italic text-secondary-x">{TIERS.find((t) => t.key === tier)?.name}</span>
          </h2>
          <p className="mt-6 text-secondary-x leading-relaxed">
            Drop your email and we&apos;ll send the sponsor pack with payment
            details, perks and a short MOU. No commitment.
          </p>
          <form
            onSubmit={onIntent}
            data-testid="sponsor-form"
            className="mt-10 flex flex-col sm:flex-row items-stretch gap-3 max-w-lg mx-auto"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="sponsor-email"
              placeholder="you@company.com"
              className="flex-1 rounded-full border border-hairline-strong bg-glass px-5 py-3 text-primary-x placeholder:text-muted-x outline-none focus:border-primary-x transition-colors"
            />
            <button
              type="submit"
              data-testid="sponsor-submit"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity"
              style={{ background: "var(--text-primary)", color: "var(--canvas)" }}
            >
              Send sponsor pack →
            </button>
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 bg-surface border-y border-hairline">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="mb-16">
            <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
              ◇ FAQ
            </span>
            <h2 className="mt-4 font-display text-5xl md:text-6xl font-medium tracking-tight leading-[0.95] text-primary-x">
              Quick answers.
            </h2>
          </div>
          <div className="divide-y divide-hairline border-y border-hairline">
            {FAQ.map((f, i) => (
              <details
                key={i}
                data-testid={`sponsor-faq-${i}`}
                className="group py-6"
              >
                <summary className="flex items-center justify-between gap-6 cursor-pointer list-none">
                  <span className="font-display text-xl md:text-2xl text-primary-x">
                    {f.q}
                  </span>
                  <span className="text-muted-x text-2xl transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-5 text-secondary-x leading-relaxed max-w-2xl">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CallToAction
        eyebrow="◇ Or — work with us"
        title="Need us to build something specific?"
        body="If you'd rather hire the studio for paid client work, start a brief instead."
        ctaLabel="Start a project"
        ctaTo="/contact"
        secondaryLabel="See our work"
        secondaryTo="/work"
      />
    </PageShell>
  );
}
